import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { getIncidentTypes, createIncidentType, updateIncidentType } from "../../lib/api";

function Toggle({ checked, onChange, label, testid }) {
  return (
    <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
      <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} />
      <span>{label}</span>
    </label>
  );
}

export function IncidentTypesTab() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState("");
  const [newRequiresGroup, setNewRequiresGroup] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState("");

  const load = async () => {
    try {
      const res = await getIncidentTypes();
      setTypes(res.data);
    } catch {
      toast.error("Failed to load incident types");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const patch = async (t, data) => {
    try {
      await updateIncidentType(t.id, data);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update type");
    }
  };

  const saveRename = async (t) => {
    const label = editLabel.trim();
    if (!label) return;
    setEditingId(null);
    if (label !== t.label) await patch(t, { label });
  };

  const addType = async () => {
    const label = newLabel.trim();
    if (!label) return;
    setSaving(true);
    try {
      await createIncidentType({ label, requires_group: newRequiresGroup });
      toast.success(`Type "${label}" added`);
      setNewLabel("");
      setNewRequiresGroup(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add type");
    } finally {
      setSaving(false);
    }
  };

  const active = types.filter((t) => !t.archived);
  const archived = types.filter((t) => t.archived);

  const Row = ({ t }) => (
    <div className="border rounded-lg px-3 py-3 space-y-2" data-testid={`incident-type-row-${t.id}`}>
      <div className="flex items-center justify-between gap-2">
        {editingId === t.id ? (
          <div className="flex items-center gap-2 flex-1">
            <Input
              value={editLabel}
              onChange={(e) => setEditLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") saveRename(t); if (e.key === "Escape") setEditingId(null); }}
              className="h-8 text-sm"
              autoFocus
              data-testid={`incident-type-edit-input-${t.id}`}
            />
            <Button size="sm" className="h-8 bg-[#00305E] hover:bg-[#00305E]/90" onClick={() => saveRename(t)} data-testid={`incident-type-save-${t.id}`}>Save</Button>
            <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditingId(null)}>Cancel</Button>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="text-sm font-medium truncate">{t.label}</span>
            {t.system && <Badge variant="outline" className="text-[10px]">System</Badge>}
            {t.archived && <Badge className="text-[10px] bg-slate-200 text-slate-700">Archived</Badge>}
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setEditingId(t.id); setEditLabel(t.label); }} data-testid={`incident-type-rename-${t.id}`}>Rename</Button>
          </div>
        )}
        <Button
          size="sm"
          variant="ghost"
          className={`h-7 text-xs ${t.archived ? "text-emerald-600" : "text-amber-600"}`}
          onClick={() => patch(t, { archived: !t.archived })}
          data-testid={`incident-type-archive-${t.id}`}
        >
          {t.archived ? "Restore" : "Archive"}
        </Button>
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1 pt-1">
        <Toggle checked={t.counts_toward_totals} onChange={(v) => patch(t, { counts_toward_totals: v })} label="Counts toward student totals" testid={`incident-type-counts-${t.id}`} />
        <Toggle checked={t.pattern_detection} onChange={(v) => patch(t, { pattern_detection: v })} label="In bullying/harassment detection" testid={`incident-type-pattern-${t.id}`} />
        <Toggle checked={t.requires_group} onChange={(v) => patch(t, { requires_group: v })} label="Requires group selection" testid={`incident-type-reqgroup-${t.id}`} />
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="incident-types-tab">
      <Card>
        <CardHeader>
          <CardTitle>Incident Types</CardTitle>
          <CardDescription>
            These categories appear in the incident form. Renaming a type only changes its display label — historical records and reports stay intact. Types can be archived (hidden from the form) but never deleted.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <>
              <div className="space-y-2">
                {active.map((t) => <Row key={t.id} t={t} />)}
              </div>

              {archived.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2 text-muted-foreground">Archived (hidden from the form, still on records)</h4>
                  <div className="space-y-2">
                    {archived.map((t) => <Row key={t.id} t={t} />)}
                  </div>
                </div>
              )}

              <div className="pt-3 border-t space-y-2">
                <h4 className="text-sm font-semibold text-muted-foreground">Add a new type</h4>
                <div className="flex gap-2 items-center flex-wrap">
                  <Input
                    placeholder="New type (e.g. Elopement)"
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") addType(); }}
                    className="max-w-xs"
                    data-testid="incident-type-new-input"
                  />
                  <Toggle checked={newRequiresGroup} onChange={setNewRequiresGroup} label="Requires group selection" testid="incident-type-new-reqgroup" />
                  <Button onClick={addType} disabled={saving || !newLabel.trim()} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="incident-type-add-btn">
                    + Add Type
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">New types count toward student totals by default and are excluded from bullying/harassment detection. Adjust the toggles after adding.</p>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
